<script setup lang="ts">
import { computed } from 'vue'

import EitherLinkOrChild from '@satch-ui/components/EitherLinkOrChild.vue'
import { t } from '@satch-ui/translations/translations'
import { localizeUrl } from '@shared/fondof-fn/url/localize'
import type { LineItemProps } from '@storefront-satch/components/molecules/product/line/types'

import { vE2eTestId } from '@fond-of/telemetry'

const props = defineProps<LineItemProps>()

defineEmits<{
  addItem: [id: string, quantity: number]
  removeItem: [id: string, quantity: number]
  changeCustomQuantityItem: [id: string, quantity: number]
}>()

const itemLink = computed(() => {
  if (!props.line.variant.product?.url) {
    return undefined
  }
  return localizeUrl(props.line.variant.product.url)
})
</script>

<template>
  <div
    v-e2e-test-id="'lineItem'"
    class="relative grid w-full grid-cols-[max-content_minmax(0,1fr)] gap-y-4 lg:gap-x-4"
    :class="props.class"
    :data-sku="props.line.variant.sku"
  >
    <EitherLinkOrChild
      class="bg-gray-ultra-light side-cart:max-w-[7rem] relative -left-5 flex aspect-square h-40 w-full max-w-[7rem] items-center justify-center overflow-hidden object-contain md:max-w-[10rem] lg:static lg:left-0"
      :href="itemLink"
      :condition="linked && canEdit"
    >
      <img
        v-if="props.line.variant.product?.media?.default?.url"
        class="max-h-full w-full"
        :src="props.line.variant.product?.media?.default?.url"
        :alt="props.line.variant.product?.media?.default?.altText ?? ''"
      />
    </EitherLinkOrChild>

    <div class="flex flex-1 flex-col flex-wrap justify-center">
      <EitherLinkOrChild
        :href="itemLink"
        :condition="linked && canEdit"
      >
        <div
          class="font-sans-bold mb-1 hyphens-auto break-words text-sm text-black lg:mb-2"
        >
          {{ props.line.variant.product?.attributes?.model }}
        </div>
      </EitherLinkOrChild>

      <div class="text-2xs flex w-full flex-wrap font-sans">
        <div
          v-if="props.line.variant.displaySize"
          class="mb-0.5 inline w-full lg:mb-1"
        >
          <span class="text-gray-medium w-auto pr-1.5">
            {{ t('cart.cartItems.attributes.size') }}
          </span>

          <span class="w-fit text-xs text-black">
            {{ props.line.variant.displaySize }}
          </span>
        </div>

        <div class="mb-0.5 inline w-full lg:mb-1">
          <span class="text-gray-medium w-auto pr-1.5">
            {{ t('cart.cartItems.attributes.style') }}
          </span>

          <span class="w-auto text-xs text-black">
            {{ props.line.variant.product?.attributes?.style }}
          </span>
        </div>

        <div
          v-if="!canEdit"
          class="mb-0.5 inline w-full lg:mb-1"
        >
          <span class="text-gray-medium w-auto pr-1.5">
            {{ t('cart.cartItems.attributes.quantity') }}
          </span>

          <span class="w-fit text-xs text-black">
            {{ props.line.quantity }}x
          </span>
        </div>
      </div>

      <slot
        name="price"
        :quantity="props.line.quantity"
      />
    </div>
  </div>

  <div
    v-if="errorMessage"
    class="text-red w-full pt-5 text-sm"
  >
    {{ errorMessage }}
  </div>
</template>
